// ============================================
// URL <-> currentPage routing helpers (v5.37.5, F1/F11)
// ----------------------------------------------------------------------------
// QuoteMaker's page state lives in React (currentPage useState), not React
// Router. Direct URL navigation, browser reload on a non-root path, and
// back/forward buttons all USED to fall back to the dashboard because nothing
// synced location.pathname with currentPage. UAT 2026-05-20 (F1/F11) repro'd:
// navigate to /intake via the menu -> click reload -> land on dashboard with
// /intake still in URL bar (mode-confusion).
//
// These pure helpers back the sync layer that lives in App.jsx:
//   - On user-auth: reads location.pathname, maps to a routable page.
//   - On currentPage change (via menu click): pushes the new URL to history.
//   - On popstate (back/forward): reads the URL, maps back to a page.
//
// Stays simple - no React Router dependency, no route params (per-page query
// strings stay independent), no nested routes.
//
// Extracted verbatim from App.jsx (TS-060 / DEC-COUNCIL-015 T1). Behaviour is
// unchanged; App.jsx re-imports these symbols.
// ============================================
export const ROUTABLE_PAGES: ReadonlySet<string> = new Set([
    'dashboard', 'quotemaker', 'quotes', 'intake', 'clients', 'receivables',
    'business-training', 'aimodels', 'audit', 'developer',
    'component-library', 'settings',
    // Added 2026-07-28. Its absence meant /analysis silently resolved to '/',
    // so the page could not be deep-linked, bookmarked, or reached by Back.
    'analysis',
    'quote-price-list',
    // Reopen a produced quote without rerunning the pipeline.
    'quick-edit',
    // Turn an existing quote document into a Job (migration 120).
    'import-quote',
    // The operator's to-do list (SPEC-tasks-page.md S1). Deep-linkable so the
    // page survives reload and Back even before it earns a rail row.
    'tasks',
    // People administration (migration 090): invite, set access, reset passwords.
    'users',
    // The site assessor's surface. Deep-linkable so a phone that reloads mid
    // visit, or a link the office sends, lands back on the same screen.
    'site-visits',
    // The QuoteMaker operator's estate. Routable like any other page: the
    // ROUTABLE_PAGES set decides whether a URL survives a reload, NOT who may
    // see the screen. Access is the 'estate' area, which is platformOnly in
    // access-areas and re-checked by the route, so a customer who types the
    // path reaches a page that answers 403 rather than a hidden door.
    'estate',
    // The Quote Brain report (slice LIVE), linked from its emails. Routable
    // does not mean readable: the server answers 404 to every account but
    // the one its platform setting names.
    'quote-brain-report',
    // The Quote Run is a page of its own (Peter, 2026-11-02: not a popup).
    // App keeps the global File Explorer open exactly while it is the page.
    'quote-run',
]);

export function pageToPath(page: string | null | undefined): string {
    if (!page || page === 'dashboard') return '/';
    return ROUTABLE_PAGES.has(page) ? `/${page}` : '/';
}

export function pathToPage(pathname: string | null | undefined): string | null {
    const slug = String(pathname || '/').replace(/^\//, '').split(/[/?#]/)[0];
    if (!slug) return 'dashboard';
    return ROUTABLE_PAGES.has(slug) ? slug : null;
}

/**
 * True when an address names this page, as the router maps both. Every reload
 * (a deploy's service-worker reload, the stale-chunk recovery, signing back
 * in) lands on the address, so the address must name the page on screen.
 */
export function addressNamesPage(pathname: string | null | undefined, page: string | null | undefined): boolean {
    return pageToPath(pathToPage(pathname)) === pageToPath(page);
}

/**
 * Pages the operator opens to WATCH or tune the platform while an automatic
 * Summary runs (Developer Tools' Live Monitor, AI Models). They hold no
 * project, edit no evidence and run nothing on this project, so a trip there
 * is not a choice about the run. Real RFQ 903025 (2026-10-18): the operator
 * opened both during the Summary and its continuation was cancelled with no
 * message.
 *
 * Deliberately a short allow-list. Every other page (dashboard, clients, intake,
 * settings, ...) is operator navigation. Adding a page here is a decision that
 * it can never change the evidence or the project. Moved from
 * attended-risk-intent.ts with the old pipeline's removal.
 */
export const PASSIVE_MONITORING_PAGES: ReadonlySet<string> = new Set(['developer', 'aimodels']);

export function isPassiveMonitoringPage(page: unknown): boolean {
    return PASSIVE_MONITORING_PAGES.has(String(page || ''));
}

/** Pages an automatic Summary may still paint for: QuoteMaker itself, or a passive page. */
export function isAttendedHandoffPage(page: unknown): boolean {
    return String(page || '') === 'quotemaker' || isPassiveMonitoringPage(page);
}

/**
 * Is moving from one page to the other a passive monitoring trip: out of
 * QuoteMaker to a passive page, back from one, or between two of them?
 * Everything else is operator navigation.
 */
export function isPassiveMonitoringExcursion(fromPage: unknown, toPage: unknown): boolean {
    const from = String(fromPage || '');
    const to = String(toPage || '');
    if (!from || !to || from === to) return false;
    return isAttendedHandoffPage(from) && isAttendedHandoffPage(to);
}
